import React, {
  FC, useEffect, useRef, useState,
} from "react";
import throttle from "lodash.throttle";
import { navigate } from "gatsby";
import { ButtonColour, SmartBuyerBenefits } from "@trussle/external-components";
import { IContentfulRichText } from "../../../../types/IContentfulRichText";
import { CalculatorTemplate } from "../../../../templates/CalculatorTemplate";
import { trackEvent } from "../../../../helpers/analytics";
import SmartBuyerForm from "./forms/SmartBuyerForm";
import { SmartBuyerCalculatorResults } from "./SmartBuyerCalculatorResults";
import { convertCurrencyStringToFloat } from "../calculatorMethods";
import "./styles.scss";
import { calculateResult } from "./calculateResult";
import { SMART_BUYER_CALCULATOR_INITIAL_PROPERTY_VALUE } from "./data";
import { CalculatorTemplateV2 } from "../../../../templates/CalculatorTemplateV2";
import { SmartBuyerCalculatorResultsV2 } from "./SmartBuyerCalculatorResultsV2";
import SmartBuyerFormV2 from "./forms/SmartBuyerFormV2";

const DEBOUNCE_RESULTS = 1000;

export interface ISmartBuyerCalculatorPanelProps {
    id: string;
    title: string;
    description: IContentfulRichText;
    disclaimer: IContentfulRichText;
    ctaText: string;
    ctaUrl: string;
    secondCtaText: string;
    secondCtaUrl: string;
    fastestLender: IContentfulRichText;
    betterV2: boolean;
}

export const SmartBuyerCalculatorPanel: FC<ISmartBuyerCalculatorPanelProps> = ({
  id,
  title,
  description,
  disclaimer,
  ctaText,
  ctaUrl,
  secondCtaText,
  secondCtaUrl,
  fastestLender,
  betterV2,
}) => {
  const [buyingType, setBuyingType] = useState("0");
  const [propertyType, setPropertyType] = useState("0");
  const [propertyValue, setPropertyValue] = useState(SMART_BUYER_CALCULATOR_INITIAL_PROPERTY_VALUE);
  const [smartBuyerResult, setSmartBuyerResult] = useState(0);
  const legalResultRef = useRef(0);
  const surveResultRef = useRef(0);

  const updateResult = () => {
    if (propertyValue !== "") {
      const convertedPropertyValue = convertCurrencyStringToFloat(propertyValue);
      const [legal, survey] = calculateResult(convertedPropertyValue, +propertyType, +buyingType);
      legalResultRef.current = legal;
      surveResultRef.current = survey;
      setSmartBuyerResult(legal + survey);
    } else {
      legalResultRef.current = 0;
      surveResultRef.current = 0;
      setSmartBuyerResult(0);
    }
  };

  const throttleResult = throttle(updateResult, DEBOUNCE_RESULTS, {
    leading: false,
    trailing: true,
  });

  useEffect(() => {
    throttleResult();
  }, [buyingType, propertyType, propertyValue]);

  return (
    <>
      {betterV2 ? (
        <div className="smartBuyer-panel">
          <SmartBuyerBenefits />
          <CalculatorTemplateV2
            id={id}
            tabInformation={[
              {
                title: "",
                form: (
                  <SmartBuyerFormV2
                    buyingType={buyingType}
                    setBuyingType={setBuyingType}
                    propertyType={propertyType}
                    setPropertyType={setPropertyType}
                    propertyValue={propertyValue}
                    setPropertyValue={setPropertyValue}
                  />),
                calculatorResults: {
                  cta: {
                    title: ctaText,
                    url: ctaUrl,
                    color: ButtonColour.DarkYellow,
                    handleClick: (e) => {
                      e.preventDefault();
                      trackEvent("First CTA Clicked", {
                        module: "Smart Buyer Calculator",
                      });
                      navigate(ctaUrl);
                    },
                  },
                  footer: {},
                  top: {
                    result: smartBuyerResult,
                  },
                  TopResultComponent: () => (
                    <SmartBuyerCalculatorResultsV2
                      legal={legalResultRef.current}
                      survey={surveResultRef.current}
                    />
                  ),
                },
              },
            ]}
          />
        </div>
      ) : (
        <CalculatorTemplate
          id={id}
          title={title}
          description={description}
          disclaimer={disclaimer}
          tabInformation={[
            {
              title: "",
              form: (
                <SmartBuyerForm
                  buyingType={buyingType}
                  setBuyingType={setBuyingType}
                  propertyType={propertyType}
                  setPropertyType={setPropertyType}
                  propertyValue={propertyValue}
                  setPropertyValue={setPropertyValue}
                />),
              calculatorResults: {
                bottom: {},
                cta: {
                  title: ctaText,
                  url: ctaUrl,
                  handleClick: (e) => {
                    e.preventDefault();
                    trackEvent("First CTA Clicked", {
                      module: "Smart Buyer Calculator",
                    });
                    navigate(ctaUrl);
                  },
                },
                secondCta: {
                  title: secondCtaText,
                  url: secondCtaUrl,
                  handleClick: (e) => {
                    e.preventDefault();
                    trackEvent("Second CTA Clicked", {
                      module: "Smart Buyer Calculator",
                    });
                    navigate(secondCtaUrl);
                  },
                },
                secondCtaColor: ButtonColour.Secondary,
                footer: {
                  heading: "Next steps",
                  text: fastestLender,
                },
                middle: {},
                secondMiddle: {},
                top: {
                  result: smartBuyerResult,
                },
                TopResultComponent: () => (
                  <SmartBuyerCalculatorResults
                    legal={legalResultRef.current}
                    survey={surveResultRef.current}
                  />
                ),
              },
            },
          ]}
          defaultTab={0}
        />
      )}
    </>
  );
};
